
if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.isUpdatingDependentSize = false;
      }

      get selectedOptionValues() {
        return Array.from(
          this.querySelectorAll(
            'select option[selected], fieldset input:checked'
          )
        ).map(({ dataset }) => dataset.optionValueId);
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT'
          ? target.selectedOptions[0]
          : target;
      }

      connectedCallback() {
        if (this._variantSelectsConnected) return;
        this._variantSelectsConnected = true;

        this.addEventListener('change', (event) => {
          const changedGroup = event.target.closest(
            '.product-form__input'
          );

          const label =
            changedGroup?.querySelector('.font-body-bold');

          const changedName = label
            ? label.textContent
                .trim()
                .replace(/:$/, '')
                .toLowerCase()
            : '';

          // Filter sizes only when Gender changes.
          if (
            changedName === 'gender' &&
            !this.isUpdatingDependentSize
          ) {
            this.filterSizeOptions();
          }

          // Preserve Sleek's normal variant update event for
          // Color, Gender, Size, and every other option.
          const target = this.getInputForEventTarget(event.target);

          if (!target) return;

          this.updateSelectedSwatchValue(event);

          FoxTheme.pubsub.publish(
            FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
            {
              data: {
                event,
                target,
                selectedOptionValues: this.selectedOptionValues,
              },
            }
          );
        });

        this.filterSizeOptions();
      }

      getOptionGroup(name) {
        return Array.from(
          this.querySelectorAll('.product-form__input')
        ).find((group) => {
          const label = group.querySelector('.font-body-bold');

          return (
            label &&
            label.textContent
              .trim()
              .replace(/:$/, '')
              .toLowerCase() === name.toLowerCase()
          );
        });
      }

      getGroupValue(group) {
        if (!group) return '';

        const radio = group.querySelector(
          'input[type="radio"]:checked'
        );

        if (radio) return radio.value;

        const select = group.querySelector('select');

        return select ? select.value : '';
      }

      getOptionPosition(name) {
        const normalizedName = name.toLowerCase();

        const label = Array.from(
          this.querySelectorAll(
            '.product-form__input .font-body-bold'
          )
        ).find(
          (element) =>
            element.textContent
              .trim()
              .replace(/:$/, '')
              .toLowerCase() === normalizedName
        );

        if (!label) return -1;

        const group = label.closest('.product-form__input');

        // Radio inputs include the Shopify option position
        // in their name, for example Color-1 or Gender-2.
        const radio = group?.querySelector(
          'input[type="radio"]'
        );

        const radioMatch = radio?.name?.match(/-(\d+)$/);

        if (radioMatch) {
          return Number(radioMatch[1]) - 1;
        }

        // Dropdowns use names such as options[Size].
        // Match the option name against the product's option order.
        const variantsElement = this.querySelector(
          '[data-product-variants]'
        );

        if (!variantsElement) return -1;

        try {
          const variants = JSON.parse(
            variantsElement.textContent
          );

          const firstVariant = variants[0];

          if (firstVariant?.options) {
            const productOptions = firstVariant.options;

            const optionNames = Array.from(
              this.querySelectorAll('.product-form__input')
            ).map((optionGroup) =>
              optionGroup
                .querySelector('.font-body-bold')
                ?.textContent.trim()
                .replace(/:$/, '')
            );

            const position = optionNames.findIndex(
              (optionName) =>
                optionName?.toLowerCase() === normalizedName
            );

            if (position >= 0) return position;
          }
        } catch (error) {
          console.error(
            'Unable to determine product option position.',
            error
          );
        }

        return Array.from(
          this.querySelectorAll('.product-form__input')
        ).indexOf(group);
      }

      filterSizeOptions() {
        const genderGroup = this.getOptionGroup('Gender');
        const sizeGroup = this.getOptionGroup('Size');
        const sizeSelect = sizeGroup?.querySelector('select');
        const variantsElement = this.querySelector(
          '[data-product-variants]'
        );

        if (
          !genderGroup ||
          !sizeGroup ||
          !sizeSelect ||
          !variantsElement
        ) {
          return;
        }

        let variants;

        try {
          variants = JSON.parse(variantsElement.textContent);
        } catch (error) {
          console.error('Unable to read product variants.', error);
          return;
        }

        if (!Array.isArray(variants) || !variants.length) return;

        const gender = this.getGroupValue(genderGroup);
        const previousSize = sizeSelect.value;

        const genderPosition = this.getOptionPosition('Gender');
        const sizePosition = this.getOptionPosition('Size');

        if (genderPosition < 0 || sizePosition < 0) return;

        const getOptions = (variant) =>
          variant.options || [
            variant.option1,
            variant.option2,
            variant.option3,
          ];

        const matchingVariants = variants.filter(
          (variant) =>
            getOptions(variant)[genderPosition] === gender
        );

        const validSizes = new Set(
          matchingVariants
            .map(
              (variant) =>
                getOptions(variant)[sizePosition]
            )
            .filter(Boolean)
        );

        if (!validSizes.size) return;

        if (!sizeSelect._originalSizeOptions) {
          sizeSelect._originalSizeOptions = Array.from(
            sizeSelect.options
          ).map((option) => option.cloneNode(true));
        }

        const masterOptions = sizeSelect._originalSizeOptions;

        const placeholder = masterOptions.find(
          (option) => option.value === ''
        );

        const validOptions = masterOptions.filter(
          (option) => validSizes.has(option.value)
        );

        const desiredValues = [
          ...(placeholder ? [''] : []),
          ...validOptions.map((option) => option.value),
        ];

        const currentValues = Array.from(
          sizeSelect.options
        ).map((option) => option.value);

        const unchanged =
          currentValues.length === desiredValues.length &&
          currentValues.every(
            (value, index) => value === desiredValues[index]
          );

        if (!unchanged) {
          sizeSelect.replaceChildren();

          if (placeholder) {
            sizeSelect.appendChild(
              placeholder.cloneNode(true)
            );
          }

          validOptions.forEach((option) => {
            sizeSelect.appendChild(option.cloneNode(true));
          });
        }

        const nextSize = validSizes.has(previousSize)
          ? previousSize
          : validOptions[0]?.value || '';

        sizeSelect.value = nextSize;

        Array.from(sizeSelect.options).forEach((option) => {
          option.toggleAttribute('selected', option.selected);
        });

        const selectedLabel = sizeGroup.querySelector(
          '[data-selected-swatch-value]'
        );

        if (selectedLabel && sizeSelect.selectedOptions.length) {
          selectedLabel.textContent =
            sizeSelect.selectedOptions[0].textContent.trim();
        }

        // Publish the normal theme event only when filtering
        // actually requires a different size.
        if (sizeSelect.value !== previousSize) {
          this.isUpdatingDependentSize = true;

          try {
            sizeSelect.dispatchEvent(
              new Event('change', { bubbles: true })
            );
          } finally {
            this.isUpdatingDependentSize = false;
          }
        }
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (
          tagName === 'SELECT' &&
          target.selectedOptions.length
        ) {
          Array.from(target.options)
            .filter((option) =>
              option.hasAttribute('selected')
            )
            .forEach((option) => {
              option.removeAttribute('selected');
            });

          target.selectedOptions[0].setAttribute(
            'selected',
            'selected'
          );

          const swatchValue =
            target.selectedOptions[0].dataset
              .optionSwatchValue;

          const swatch = target
            .closest('.product-form__input')
            ?.querySelector('[data-selected-value] > .swatch');

          if (!swatch) return;

          if (swatchValue) {
            swatch.style.setProperty(
              '--swatch--background',
              swatchValue
            );
            swatch.classList.remove('swatch--unavailable');
          } else {
            swatch.style.setProperty(
              '--swatch--background',
              'unset'
            );
            swatch.classList.add('swatch--unavailable');
          }

          swatch.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset
              .optionSwatchFocalPoint || 'unset'
          );
        } else if (
          tagName === 'INPUT' &&
          target.type === 'radio'
        ) {
          const selectedValue = target
            .closest('.product-form__input')
            ?.querySelector('[data-selected-value]');

          if (selectedValue) {
            selectedValue.textContent = value;
          }
        }
      }
    }
  );
}
